<template>
  <Dialog
    width="644px"
    height="unset"
    titleAlign="left"
    :title="$$t('create.create_similar')"
    customClass="basic-mode-similar-dialog"
    :visible="renderData.visible"
    @close="closeDialog"
    mobileRenderType="drawerDialog"
  >
    <div
      class="height-all create-easy-mode-similar flex-column"
      :class="{
        'create-easy-mode-similar--mobile': createRenderType == 'mobile',
      }"
    >
      <div class="create-easy-mode-similar-edit flex" @click="onInputAreaClick">
        <textarea
          ref="refInput"
          :placeholder="$$t('create.idear_placehoder')"
          class="flex1 width-0"
          :value="renderData.prompt"
          @input="onInput"
          @change="onChange"
        />

        <div class="create-easy-mode-similar-edit-count">
          {{ renderData.prompt?.length ?? 0 }}/ {{ MAX_TITLE_LENGTH }}
        </div>
      </div>

      <div class="create-easy-mode-similar-done-btn-box">
        <div class="create-easy-mode-similar-credits" @click="goBuyCredits">
          <span class="create-submit-credits-text fn-inline"
            >{{ $$t("create.credits_remaining") }}
            {{ renderData.userCredits }}</span
          >
        </div>
        <div class="create-easy-mode-similar-done-btn">
          <Button
            :loading="renderData.isLoading"
            @click="onDoneClick"
            :renderStyle="{
              '--padding': '0 35px',
              '--width': 'max-content',
              '--height': '48px',
              '--borderRadius': '50px',
              '--fontColor': '#000',
              '--fontSize': '16px',
              '--fontWeight': 'normal',
              '--border': 'none',
              '--background':
                'linear-gradient(120deg, rgb(79, 222, 255, 1) -1%, rgb(230, 117, 255, 1) 100%)', //#94ADFF
              '--backgroundHover':
                'linear-gradient(120deg, rgb(79, 222, 255, 0.75) -1%, rgb(230, 117, 255, 0.75) 100%)',
              '--fontColorHover': '#000',
            }"
          >
            {{ $$t("create.create") }}
          </Button>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  getCurrentInstance,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import message from "@/components/functionCallComponent/message.jsx";
import feedListApi from "@/api/api/feedListApi.js";

import { $$language, $$t } from "@/i18n/i18n.js";
import Dialog from "@/components/basic/dialog/Dialog.vue";
import Button from "@/components/basic/button/Button.vue";

import { storeToRefs } from "pinia";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
import useUserDataStore from "@/store/userDataStore.js";
import useFeedListStore from "@/store/create/feedListDataStore.js";

const userDataStore = useUserDataStore();

const createLayoutStore = useCreateLayoutStore();
const feedListDataStore = useFeedListStore();
const { createRenderType } = storeToRefs(createLayoutStore);

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties;

const MAX_TITLE_LENGTH = 500;

const Router = useRouter();
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  visible: {
    type: Boolean,
    default: true,
  },
});
const emits = defineEmits([
  "update:visible",
  "update:prompt",
  "cancel",
  "goCreateResult",
]);

const refInput = ref(null);
const renderData = reactive({
  visible: props.visible,
  prompt: props.item?.prompt,
  userCredits: userDataStore.isLogin ? userDataStore.credits ?? 0 : 0,
  isLoading: false,
});

const onInputAreaClick = (e) => {
  refInput.value?.focus();
};

const onInput = (e) => {
  if (e.target.value.length > MAX_TITLE_LENGTH) {
    e.target.value = e.target.value.slice(0, MAX_TITLE_LENGTH);
  }
  renderData.prompt = e.target.value;
  emits("update:prompt", renderData.prompt);
};
const onChange = (e) => {
  emits("update:prompt", renderData.prompt);
};

const onClose = () => {
  renderData.visible = false;
  renderData.prompt = "";
  emits("update:visible", false);
  emits("cancel");
};

const onDoneClick = async () => {
  renderData.isLoading = true;
  feedListDataStore
    .doGenerateSimilarEasyMode(props.data, {
      content: renderData.prompt,
    })
    .then(() => {
      renderData.isLoading = false;
      emits("goCreateResult");
    })
    .catch((err) => {
      renderData.isLoading = false;
    });
};

const closeDialog = () => {
  renderData.visible = false;
};

const goBuyCredits = () => {
  Router.push({
    name: "subscribe",
    query: {
      membership_source1: "create",
      membership_source2: "create_basic_similar",
    },
  });
  closeDialog();
};

watch(
  () => props.visible,
  (val) => {
    renderData.visible = props.visible;
    renderData.prompt = props.data?.prompt?.content ?? "";
  }
);

watch(
  () => {
    return userDataStore.credits;
  },
  () => {
    renderData.userCredits = userDataStore.credits ?? 0;
  }
);

watch(
  () => {
    return userDataStore.isLogin;
  },
  () => {
    renderData.userCredits = userDataStore.isLogin
      ? userDataStore.credits ?? 0
      : 2;
  }
);

watch(
  () => renderData.visible,
  (val) => {
    emits("update:visible", val);
  }
);

onMounted(() => {
  renderData.visible = props.visible;
  renderData.prompt = props.data?.prompt?.content ?? "";
});
</script>
<style lang="scss">
.custom-dialog.basic-mode-similar-dialog {
  .dialog-box {
    background: linear-gradient(
      129deg,
      #3b3b96 7%,
      #2e1953 47%,
      #361469 100%
    ) !important;
  }
  &.drawer-dialog {
    .dialog-box {
      background: transparent !important;
      background-image: url(@/assets/img/main-bg-mobile-wp.webp) !important;
      background-size: 100% 100% !important;
    }
  }
}
.create-easy-mode-similar {
  padding: 13px 40px 32px 40px;

  .create-easy-mode-similar-edit {
    height: 200px;
    border-radius: 8px;
    padding: 16px 16px 14px 16px;
    background: rgba(255, 255, 255, 0.1);
    align-items: center;
    flex-direction: column;
    textarea {
      height: 100%;
      width: 100%;
      font-family: HarmonyOS Sans SC;
      font-size: 16px;
      font-weight: normal;
      line-height: 22px;
      color: #ffffff;
      border: none;
      outline: none;
      background: transparent;
      color: #ffffff;
      height: 0;
      flex: 1;
      resize: none;
      &::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }
    .create-easy-mode-similar-edit-count {
      font-size: 14px;
      font-weight: normal;
      line-height: 20px;
      text-align: right;
      color: rgba(255, 255, 255, 0.5);
      margin: 8px 0 0 0 0;
      width: 100%;
    }
  }

  .create-easy-mode-similar-done-btn-box {
    margin: 24px 0 0 0;
    padding: 0 0 0 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 48px;
    .create-easy-mode-similar-credits {
      font-family: HarmonyOS Sans SC;
      font-size: 16px;
      align-items: center;
      letter-spacing: 0.02em;
      color: #ffffff;
    }
    .create-easy-mode-similar-done-btn {
    }
    .custom-button .el-button .el-icon.is-loading {
      background-image: url(@/assets/img/loading-mini-white-3x.png) !important;
      background-size: 100% 100% !important;
    }
  }
  &.create-easy-mode-similar--mobile {
    padding: 15px 16px 16px 16px;
    .create-easy-mode-similar-done-btn-box {
      .create-easy-mode-similar-done-btn {
        margin: 16px auto 16px auto;
      }
    }
  }
}
</style>
